/**
 * Native <dialog> enhancements: closing animation + backdrop click + автофокус +
 * настраиваемые атрибуты.
 *
 * Public API:
 *   openDialog(dialogEl)         — showModal() с открывающей анимацией + автофокус
 *   closeDialog(dialogEl)        — закрытие с анимацией (data-closing → animationend → close())
 *   focusFirstInput(container)   — фокус первого видимого input/textarea/select
 *
 * Атрибуты на <dialog>:
 *   data-close-on-backdrop="true|false"   — клик по бэкдропу закрывает (default: true)
 *   data-close-on-escape="true|false"     — Esc закрывает (default: true; native поведение)
 *   data-backdrop="default|none|light"    — стиль бэкдропа (default: тёмный с blur)
 *   data-autofocus="false"                — отключить автофокус первого input (default: true)
 *
 * Атрибуты на отдельных полях (приоритет порядка):
 *   data-autofocus                        — этот элемент фокусируется в первую очередь
 *   autofocus (стандартный HTML)          — fallback второго приоритета
 *   <первый видимый input/textarea/select> — fallback по умолчанию
 *
 * CSS использует:
 *   dialog[open]                — анимация открытия (см. dialogs.css)
 *   dialog[data-closing]        — анимация закрытия
 *   dialog[data-backdrop="none"]::backdrop — без подложки
 *   dialog[data-backdrop="light"]::backdrop — светлая подложка
 */

const CLOSE_ANIMATION_FALLBACK_MS = 400;
const AUTOFOCUS_DELAY_MS = 50;
const FOCUSABLE_INPUTS_SELECTOR = [
    'input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="reset"]):not([disabled]):not([readonly])',
    'textarea:not([disabled]):not([readonly])',
    'select:not([disabled])',
].join(', ');

function isElementVisible(el) {
    if (!el || el.disabled || el.readOnly) return false;
    if (el.hidden) return false;
    // offsetParent === null если элемент или его родитель display:none
    if (el.offsetParent === null) return false;
    const style = window.getComputedStyle(el);
    if (style.visibility === 'hidden' || style.display === 'none') return false;
    return true;
}

export function focusFirstInput(container) {
    if (!container) return false;

    // Приоритет 1: явно помеченный data-autofocus
    const tagged = container.querySelector('[data-autofocus]');
    if (tagged && isElementVisible(tagged)) {
        tagged.focus();
        if (typeof tagged.select === 'function' && !tagged.value) tagged.select();
        return true;
    }

    // Приоритет 2: HTML-атрибут autofocus
    const auto = container.querySelector('[autofocus]');
    if (auto && isElementVisible(auto)) {
        auto.focus();
        return true;
    }

    // Приоритет 3: первый видимый input/textarea/select
    const inputs = container.querySelectorAll(FOCUSABLE_INPUTS_SELECTOR);
    for (const input of inputs) {
        if (isElementVisible(input)) {
            input.focus();
            return true;
        }
    }

    return false;
}

export function openDialog(dialog) {
    if (!dialog || dialog.open) return;
    delete dialog.dataset.closing;
    dialog.showModal();

    if (dialog.dataset.autofocus === 'false') return;

    // Задержка нужна, чтобы Alpine успел отрисовать x-show блоки и анимация
    // открытия не сбрасывала фокус (в некоторых браузерах focus до paint игнорируется)
    setTimeout(() => focusFirstInput(dialog), AUTOFOCUS_DELAY_MS);
}

export function closeDialog(dialog) {
    if (!dialog || !dialog.open) return;

    // Если уже идёт анимация закрытия — не запускаем повторно
    if (dialog.dataset.closing !== undefined) return;

    dialog.dataset.closing = '';

    let done = false;
    const finalize = () => {
        if (done) return;
        done = true;
        delete dialog.dataset.closing;
        dialog.close();
    };

    const onAnimEnd = e => {
        // Дожидаемся именно завершения анимации на самом dialog (не на потомках)
        if (e && e.target !== dialog) return;
        dialog.removeEventListener('animationend', onAnimEnd);
        finalize();
    };

    dialog.addEventListener('animationend', onAnimEnd);

    // Fallback: если анимация не сработала (prefers-reduced-motion, отсутствие keyframes)
    setTimeout(() => {
        dialog.removeEventListener('animationend', onAnimEnd);
        finalize();
    }, CLOSE_ANIMATION_FALLBACK_MS);
}

/**
 * Глобальная инициализация: backdrop-click и кастомизация Esc.
 * Слушает click на самом <dialog>: если target === dialog (т.е. по бэкдропу),
 * закрываем (если data-close-on-backdrop !== "false").
 *
 * Для Esc используем event 'cancel' — preventDefault + closeDialog для анимации,
 * либо preventDefault если data-close-on-escape="false".
 */
export function initDialogEnhancements() {
    document.addEventListener('click', e => {
        const dialog = e.target.closest('dialog');
        if (!dialog || !dialog.open) return;

        // Клик на содержимом — игнорируем; только клик на самом <dialog> = backdrop
        if (e.target !== dialog) return;

        if (dialog.dataset.closeOnBackdrop === 'false') return;

        closeDialog(dialog);
    });

    document.addEventListener('cancel', e => {
        const dialog = e.target;
        if (!(dialog instanceof HTMLDialogElement)) return;

        // Esc нажат — отменяем native close, чтобы запустить нашу анимацию
        e.preventDefault();

        if (dialog.dataset.closeOnEscape === 'false') return;

        closeDialog(dialog);
    });
}

// Глобальные обёртки для inline-вызовов (без импорта)
window.openDialog = openDialog;
window.closeDialog = closeDialog;
